import { SuccessPageRule } from "../../components/FormClient/FormClient.types";
import { Field, PushData, StepData } from "../../components/FormViewer/FormViewer.types";
import { Insurance, DoctorsResponse } from "../../components/Steps/InputFields/SelectDropdown/SelectDropdown.types";
import { MarketingData } from "../../interfaces";
import { getAppBFFUrl, getFieldsFormSession, redirectToSuccessPage, trackEvent } from "../functions";

export async function formatData(lead_data: Array<StepData>, push_data: Array<PushData>, marketing_data: MarketingData, formCompleted?: boolean, multiple_prescriptions?: boolean) {
  const responseBody = new FormData();

  push_data.forEach((el) => {
    const { crm_field, crm_value } = el;
    responseBody.append(crm_field, crm_value);
  });

  lead_data.forEach((step) => {
    const { fields, answer, settings } = step;
    // Get data from Form step types
    fields?.forEach((field: Field) => {
      if (field.field_label === "explicit_consent") {
        // Special case because of timestamp
        field.field_value
          && responseBody.append(
            "timestamp",
            new Date().toISOString().split(".")[0]
          );

      } else if (typeof(field.field_value) === "object" && field.field_value.crm_record_id) {
        responseBody.append(field.field_crm_id, field.field_value.crm_record_id);
      } else if (typeof(field.field_value) === "boolean") {
        responseBody.append(field.field_crm_id, String(field.field_value));
      } else if(field.field_label === "prescriptions" && multiple_prescriptions) {
        [...field.field_value].forEach((file: File)=>{
          responseBody.append(field.field_crm_id, file, file.name);
        });
      } else {
        responseBody.append(field.field_crm_id, field.field_value);
      }
    });

    // Get data from Question step types
    answer && settings?.crm_field && responseBody.append(settings.crm_field, answer.field_value);
  });

  (Object.keys(marketing_data) as (keyof MarketingData)[]).forEach((key) => {
    if (!marketing_data[key] || responseBody.get(key) !== null) return;
    responseBody.append(key, String(marketing_data[key]));
  });

  responseBody.append("is_last_step", String(formCompleted));

  if(window.sessionLayer.lead.id_){
    responseBody.append("id_", window.sessionLayer.lead.id_);
  }


  return responseBody;
}

export const handleStepSuccessEvent = async (response: Pick<Response, "status" | "json">, is_last_step: boolean, hasSuccessStep: boolean, defaultSuccessPageUrl: string, successPageRules: Array<SuccessPageRule>, stepHasEmailField?: boolean, stepHasPhoneField?: boolean) => {
  const { lead: { lead_data} } = window.sessionLayer;

  if (response.status >= 200 && response.status < 300) {

    stepHasEmailField && trackEvent("formClient.partialLeadCreated");
    stepHasPhoneField && trackEvent("formClient.leadCreated");

    if(is_last_step || hasSuccessStep) {
      const eventData = { message: "Lead submitted successfully", leadData: lead_data };
      const event = new CustomEvent('onFcSuccess', { detail: eventData });
      window.dispatchEvent(event);
      trackEvent("formClient.formSubmitted");
      !hasSuccessStep && redirectToSuccessPage(defaultSuccessPageUrl, successPageRules, lead_data);
    } else {
      const eventData = { message: "Step submitted successfully" };
      const event = new CustomEvent('onFcStepTransition', { detail: eventData });
      window.dispatchEvent(event);
    }
  } else {
    // TODO: Send unknown error message
  }
}

export const getInsurances = async (locale: string): Promise<Array<Insurance>> => {
  const baseUrl = await getAppBFFUrl();
  const pathSegment = {
    "fr-FR": "fr",
    "de-DE": "de",
    "en-EN": "de"
  };

  try {
    const res = await fetch(
      `${baseUrl}/platform-nexus/public/v2/insurances/${(pathSegment as any)[locale]}`,
      {  method: 'GET' },
    );

    const jsonData = await res.json();
    const finalData = jsonData.items;
    return finalData;
  } catch (e) {
    // Cors Error or internal server error
    return [] as Array<Insurance>;
  }
}

export const getNearbyZipCodes = async (postalCode: string, radius: number): Promise<Array<string>> => {
  const countryCode = 'DE';
  const url = `https://zip-api.eu/api/v1/radius/${countryCode}-${postalCode}/${radius}/km`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    const data = await response.json();
    const postalCodes = data.length ? data.map((el: any) => {
      return el.postal_code;
    }) : [data.postal_code];
    return postalCodes || [];
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
}

export const searchDoctors = async (
  query: string,
  filter: Record<string, any>,
  limit = 20,
  skip = 0,
): Promise<DoctorsResponse> => {
  try {
    const baseUrl = await getAppBFFUrl();

    // Local-dev convenience: the public doctors endpoint blocks cross-origin
    // requests from localhost, so calling the remote API only produces
    // unsuppressable CORS/403 console errors and an empty dropdown. When the
    // base URL is cross-origin (i.e. the request is guaranteed to fail), skip it
    // entirely and serve mock doctors so the funnel is testable without running
    // the backend. A same-origin base URL (a locally-running backend) still hits
    // the real endpoint. The NODE_ENV check is replaced with "production" at
    // build time, so this whole branch is dead-code-eliminated from the shipped
    // bundle and can never activate on the live site.
    if (process.env.NODE_ENV === "development" && isCrossOriginBaseUrl(baseUrl)) {
      return localDoctorsFallback(query, filter, limit, skip);
    }

    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (filter && Object.keys(filter).length) {
      params.set("filter", JSON.stringify(filter));
    }
    if (query) {
      params.set("search", JSON.stringify({ __match_all__: query }));
    }

    const response = await fetch(
      `${baseUrl}/platform-nexus/public/v2/doctors?${params.toString()}`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(`doctors request failed: ${response.status}`);
    }
    const jsonData = await response.json();
    return {
      items: jsonData.items || [],
      total: jsonData.total || 0,
    };
  } catch (e) {
    console.error("Error fetching doctors:", e);
    if (process.env.NODE_ENV === "development") {
      return localDoctorsFallback(query, filter, limit, skip);
    }
    return { items: [], total: 0 };
  }
};

// True when `baseUrl` points at a different origin than the page — such a
// request will be blocked by CORS. Used to short-circuit the doctors fetch in
// local dev (see searchDoctors).
const isCrossOriginBaseUrl = (baseUrl: string): boolean => {
  try {
    return new URL(baseUrl, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const localDoctorsFallback = (
  query: string,
  filter: Record<string, any>,
  limit: number,
  skip: number,
): DoctorsResponse => {
  const mk = (
    i: number,
    full_name: string,
    address: string,
    zipcode: string,
    specialty: string | null,
  ): any => ({
    id_: `mock-${i}`,
    full_name,
    doctolib_link: null,
    phone_number: `+49 30 ${1000000 + i}`,
    address,
    zipcode,
    email: `praxis${i}@example.de`,
    specialty,
    crm_record_id: `${4300000000 + i}`,
  });

  const all: any[] = [
    mk(1, "Dr. med. Anna Schmidt", "Friedrichstraße 12, 10117 Berlin", "10117", "Urologie"),
    mk(2, "Dr. med. Thomas Müller", "Maximilianstraße 5, 80539 München", "80539", "Urologie"),
    mk(3, "Dr. med. Julia Weber", "Mönckebergstraße 8, 20095 Hamburg", "20095", "Gynäkologie"),
    mk(4, "Dr. med. Stefan Fischer", "Zeil 90, 60313 Frankfurt am Main", "60313", "Urologie"),
    mk(5, "Dr. med. Katrin Becker", "Königstraße 25, 70173 Stuttgart", "70173", "Gynäkologie"),
    mk(6, "Dr. med. Michael Wagner", "Schildergasse 40, 50667 Köln", "50667", "Urologie"),
    mk(7, "Dr. med. Sabine Hoffmann", "Prager Straße 15, 01069 Dresden", "01069", "Gynäkologie"),
    mk(8, "Dr. med. Andreas Schäfer", "Bahnhofstraße 3, 30159 Hannover", "30159", "Allgemeinmedizin"),
    mk(9, "Dr. med. Petra Koch", "Kaiserstraße 60, 76133 Karlsruhe", "76133", null),
    mk(10, "Dr. med. Markus Richter", "Breite Straße 22, 40213 Düsseldorf", "40213", "Urologie"),
  ];

  let items = all;
  if (query) {
    const q = query.toLowerCase();
    items = items.filter(
      (d) => d.full_name.toLowerCase().includes(q) || d.address.toLowerCase().includes(q),
    );
  }

  return { items: items.slice(skip, skip + limit), total: items.length };
};

export const handleError = (error: unknown) => {
  return {
    res:
      new Response(null, {
      status: 500
    }),
    details: (error instanceof TypeError || error instanceof Error) ? error?.message : error
  };
};

const VALID_TENANTS = ["edera", "lutera", "mictera"];

// The product the patient is on: chosen on a product step or pre-set from
// the URL (`crm_field_product`) first, the form's own CRM product last — a
// shared form carries one default for all three apps. A `product` value that
// is not a tenant (a joined multi-value, a label) does not block the fallback.
export function getTenant(): string | null {
  const candidates = [getFieldsFormSession("product"), window.sessionLayer?.lead?.marketing_data?.product];
  return candidates.map((p) => p?.toLowerCase()).find((t) => t && VALID_TENANTS.includes(t)) ?? null;
}
